// App-wide IME-blind window size. Adaptive layout decisions (tiers,
// breakpoints, pane collapses) must consume THIS instead of raw
// useWindowSize: since the keyboard-resize fix the layout viewport shrinks
// while the on-screen keyboard is open, and reacting to that shrink can
// unmount the focused input (focus loss closes the keyboard → loop).
// Decision logic lives in src/domain/layout/stable-window-size.ts.
import { readonly, ref, watch } from 'vue';
import {
  createSharedComposable,
  useActiveElement,
  useTimeoutFn,
  useWindowSize,
} from '@vueuse/core';
import { resolveStableSize } from '@/domain/layout/stable-window-size';
import { deepActiveElement } from '@/lib/dom/deep-active-element';

/** Input types that never summon a text keyboard. */
const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'file',
  'color',
  'image',
  'hidden',
]);

/**
 * Whether the element can summon an on-screen text keyboard.
 *
 * The isConnected check is required for the resync path: vueuse 10.9's
 * useActiveElement has no triggerOnRemoval option (that shipped in vueuse
 * 11) and only updates on window focus/blur events, so when the focused
 * element is removed from the DOM without a blur (e.g. Escape closing a
 * popover that hosts the focused textarea) the ref goes stale pointing at a
 * detached node. Treating disconnected nodes as not-editable lets every
 * subsequent event self-heal instead of freezing the stable size forever.
 */
export function isTextEditable(el: Element | null | undefined): boolean {
  if (!(el instanceof HTMLElement) || !el.isConnected) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(el.type);
  }
  return false;
}

/**
 * On mobile the keyboard-close resize lands ~150–250 ms AFTER blur; an
 * immediate post-blur resync would briefly bake the shrunk height in and
 * cause a visible tier flicker. 500 ms comfortably clears that window while
 * staying imperceptible for the desktop staleness case.
 *
 * Exported because the cart compaction engine must defer its own post-blur
 * re-evaluation by the same window (any shorter and it would act on the
 * still-keyboard-squeezed DOM; any longer and it would lag the resync that
 * un-freezes the stable size on desktop).
 */
export const RESYNC_DELAY_MS = 500;

export const useStableWindowSize = createSharedComposable(() => {
  const { width: rawWidth, height: rawHeight } = useWindowSize();
  const activeElement = useActiveElement();

  const width = ref(rawWidth.value);
  const height = ref(rawHeight.value);

  // Deferred resync on focus release: heals the stable size after the
  // keyboard closes (and after desktop resizes that happened while an input
  // was focused). Re-checks focus when it fires — the user may have tapped
  // straight into another input.
  const { start: startResync, stop: cancelResync } = useTimeoutFn(
    () => {
      // Live read: the user may have tapped straight into another input.
      if (isTextEditable(deepActiveElement())) return;
      width.value = rawWidth.value;
      height.value = rawHeight.value;
    },
    RESYNC_DELAY_MS,
    { immediate: false }
  );

  // Decision reads the live focused element (same discipline as the
  // compaction focus guard): the vueuse ref depends on window focus/blur
  // event dispatch, which browsers suppress while the page lacks OS focus —
  // the live read is always correct at resize time. It descends shadow
  // roots: a field focused inside `<gpt-help>` must count as editable.
  watch([rawWidth, rawHeight], () => {
    const result = resolveStableSize(
      { width: width.value, height: height.value },
      { width: rawWidth.value, height: rawHeight.value },
      isTextEditable(deepActiveElement())
    );
    width.value = result.width;
    height.value = result.height;
  });

  watch(activeElement, (el) => {
    if (isTextEditable(el)) cancelResync();
    else startResync();
  });

  return { width: readonly(width), height: readonly(height) };
});
